/**
 * LatestChapterPill — the whisper-quiet host affordance on the wall.
 *
 * Strict constraints per Craig's brief — the wall must NEVER read as
 * a notification centre, so this pill is locked down hard:
 *
 *   • organiser-only (compares event.user_id against useAuth().user.user_id)
 *   • appears once per newly-created chapter, then dismisses on view
 *     OR expires 24h after the chapter's created_at
 *   • mutual exclusion with the keepsake pill — passed `hidden` prop
 *     when the wall is also surfacing a keepsake
 *   • respiration is GENTLER than the keepsake pill (6.5s vs 4.2s)
 *     and softer than the LIVE indicator
 *   • copy is verbatim from the brief, no numerics, no timestamps:
 *       "✦ A new chapter has settled onto the shelf"
 *
 * Interaction — spatial continuity per the brief:
 *   • does NOT hard-navigate immediately
 *   • dims the wall, soft-glows the spine destination, then
 *     transitions into /archive with `?focus={chapter_id}` so the
 *     archive can highlight the newest spine
 *   • the dim + glow phase is short (~1.4s) — long enough to feel
 *     intentional, short enough to never feel like a loading screen
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FRESH_WINDOW_MS = 24 * 60 * 60 * 1000;
const DISMISS_KEY = (eventId, chapterId) =>
  `eventdrop_chapter_pill_${eventId}_${chapterId}`;

export default function LatestChapterPill({
  eventId,
  slug,
  isHost,
  hidden = false,
  onTransitionStart,
  onTransitionEnd,
  onVisibilityChange,
}) {
  const navigate = useNavigate();
  const [chapter, setChapter] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [phase, setPhase] = useState('idle');  // 'idle' | 'dimming' | 'navigating'
  const transitionTimer = useRef(null);

  // Pull the most recent chapter. Polling lightly — the wall already
  // polls memory snaps every 8s; a chapter is created at most once
  // per event-night so we can use a quieter cadence.
  useEffect(() => {
    if (!isHost || !eventId) return;
    let cancelled = false;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/events/${eventId}/memory-chapters`);
        if (cancelled) return;
        const list = r.data?.chapters || [];
        const latest = list.length > 0 ? list[0] : null;  // already sorted desc
        setChapter(latest);
      } catch {
        if (!cancelled) setChapter(null);
      }
    };
    load();
    const id = setInterval(load, 45_000);   // 45s cadence — calm
    return () => { cancelled = true; clearInterval(id); };
  }, [eventId, isHost]);

  // Determine whether THIS chapter is still "fresh" (within 24h) and
  // has not been dismissed by the host on this device.
  const isFresh = useMemo(() => {
    if (!chapter?.created_at) return false;
    const ts = Date.parse(chapter.created_at);
    if (Number.isNaN(ts)) return false;
    return (Date.now() - ts) < FRESH_WINDOW_MS;
  }, [chapter]);

  useEffect(() => {
    if (!chapter?.chapter_id || !eventId) return;
    try {
      if (localStorage.getItem(DISMISS_KEY(eventId, chapter.chapter_id))) {
        setDismissed(true);
      } else {
        setDismissed(false);
      }
    } catch { /* ignore */ }
  }, [chapter?.chapter_id, eventId]);

  useEffect(() => () => {
    if (transitionTimer.current) clearTimeout(transitionTimer.current);
  }, []);

  if (!isHost) return null;
  if (hidden) return null;
  if (!chapter || !isFresh || dismissed) {
    // Report not-visible up so peer pills (e.g. ShareWallPill) can
    // surface themselves.
    if (onVisibilityChange) onVisibilityChange(false);
    return null;
  }
  if (onVisibilityChange) onVisibilityChange(true);

  const handleTap = () => {
    if (phase !== 'idle') return;
    try { localStorage.setItem(DISMISS_KEY(eventId, chapter.chapter_id), '1'); } catch { /* ignore */ }
    setPhase('dimming');
    if (onTransitionStart) onTransitionStart(chapter);
    // First beat — wall dims + a soft "settling…" line shows.
    transitionTimer.current = setTimeout(() => {
      setPhase('navigating');
      // Second beat — navigate into the archive, carrying the
      // chapter id so the destination spine can glow.
      navigate(`/event/${slug}/archive?focus=${chapter.chapter_id}`);
      if (onTransitionEnd) onTransitionEnd(chapter);
    }, 1400);
  };

  return (
    <>
      <button
        type="button"
        onClick={handleTap}
        data-testid="memory-wall-chapter-pill"
        aria-label="A new chapter has settled onto the shelf"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 10,
          padding: '10px 18px',
          borderRadius: 999,
          background: 'rgba(20,12,6,0.62)',
          border: '1px solid rgba(212,175,94,0.26)',
          backdropFilter: 'blur(12px)',
          color: 'rgba(255,235,210,0.80)',
          fontFamily: 'Georgia, "Times New Roman", serif',
          fontStyle: 'italic',
          fontSize: 12.5,
          letterSpacing: '0.01em',
          cursor: phase === 'idle' ? 'pointer' : 'wait',
          boxShadow: '0 8px 22px rgba(0,0,0,0.36)',
          // Gentler respiration than the keepsake pill (6.5s vs 4.2s)
          animation: 'chapterPillBreathe 6.5s ease-in-out infinite',
          transition: 'transform 320ms ease, color 320ms ease, border-color 320ms ease, opacity 480ms ease',
          opacity: phase === 'idle' ? 1 : 0,
        }}
        onMouseEnter={(e) => {
          if (phase !== 'idle') return;
          e.currentTarget.style.color = '#FFE7A8';
          e.currentTarget.style.borderColor = 'rgba(212,175,94,0.48)';
          e.currentTarget.style.transform = 'translateY(-1px)';
        }}
        onMouseLeave={(e) => {
          if (phase !== 'idle') return;
          e.currentTarget.style.color = 'rgba(255,235,210,0.80)';
          e.currentTarget.style.borderColor = 'rgba(212,175,94,0.26)';
          e.currentTarget.style.transform = 'none';
        }}
      >
        <span aria-hidden style={{ fontSize: 13, color: '#D4AF5E' }}>✦</span>
        A new chapter has settled onto the shelf
      </button>

      {/* SPATIAL TRANSITION OVERLAY — dim the wall + a single quiet
          serif line so the move into the archive feels physically
          adjacent, not like a route change. */}
      {phase !== 'idle' && (
        <div
          data-testid="chapter-pill-transition"
          aria-hidden
          style={{
            position: 'fixed', inset: 0, zIndex: 250,
            background: 'rgba(8,4,1,0.78)',
            backdropFilter: 'blur(6px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            pointerEvents: 'none',
            animation: 'chapterTransitionFade 480ms ease-out',
          }}
        >
          <div style={{
            fontFamily: 'Georgia, "Times New Roman", serif',
            fontStyle: 'italic',
            fontSize: 15,
            color: 'rgba(212,175,94,0.78)',
            letterSpacing: '0.04em',
            textAlign: 'center',
          }}>
            settling onto the shelf ✦
          </div>
        </div>
      )}

      <style>{`
        @keyframes chapterPillBreathe {
          0%, 100% { box-shadow: 0 8px 22px rgba(0,0,0,0.36), 0 0 0 0 rgba(212,175,94,0.0); }
          50%      { box-shadow: 0 8px 22px rgba(0,0,0,0.36), 0 0 18px 1px rgba(212,175,94,0.14); }
        }
        @keyframes chapterTransitionFade {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
      `}</style>
    </>
  );
}
